/** デバイス管理の画面で使う定数。状態 → tone の対応は StatusTag の流儀でここに 1 か所。 */
import type { RegistryPolicy, RegistryTarget, RegistryUsers } from "../../lib/api";
import type { Tone } from "../../ui/StatusTag";

/** 取得元(`devices.source`)。どの経路で台帳に入った行かで出すタブも変わる。 */
export const SOURCES = [
  { value: "agent", label: "エージェント", color: "blue" },
  { value: "mdm", label: "MDM", color: "purple" },
  { value: "android", label: "Android", color: "green" },
];

export function sourceLabel(v: string): string {
  return SOURCES.find((s) => s.value === v)?.label ?? v;
}

export function sourceColor(v: string): string {
  return SOURCES.find((s) => s.value === v)?.color ?? "default";
}

/** ポリシー 1 件の適用結果(`policy_state` の値)→ tone。applied 以外は目立たせる。 */
export function policyStateTone(state: string): Tone {
  if (state === "applied") return "success";
  if (state === "error") return "danger";
  return "warning";
}

/**
 * 端末の状態 → tone。台帳に「状態」列は無いので、ポリシーの適用具合で読む
 * (全部適用 = success / 一部 = warning / ポリシー無し = neutral)。
 */
export function deviceStatusTone(applied: number, total: number): Tone {
  if (!total) return "neutral";
  return applied === total ? "success" : "warning";
}

/** レジストリ配布の対象(AG2)。何も選んでいない = 配らない。 */
export const EMPTY_TARGET: RegistryTarget = { all: false, groups: [], devices: [] };
export const ALL_TARGET: RegistryTarget = { all: true, groups: [], devices: [] };

/** 当たる理由(all / device / group:<id>)を人の言葉に。 */
export function reasonLabel(reason: string, groupNames: Record<string, string>): string {
  if (reason === "all") return "全端末";
  if (reason === "device") return "この端末を個別に指定";
  if (reason.startsWith("group:")) {
    const id = reason.slice(6);
    return `グループ「${groupNames[id] ?? "#" + id}」`;
  }
  return "当たらない";
}

/** HKCU の当て先の選択肢(サーバーの REGISTRY_USERS と同じ)。 */
export const USERS_OPTIONS: { value: RegistryUsers; label: string }[] = [
  { value: "logged_on", label: "ログオン中のユーザー" },
  { value: "non_admins", label: "管理者以外の全アカウント" },
];

/** HKCU の行の当て先の表示名。HKLM の行は null(当て先は無い)。 */
export function usersLabel(p: Pick<RegistryPolicy, "hive" | "users">): string | null {
  if (p.hive !== "HKCU") return null;
  return USERS_OPTIONS.find((o) => o.value === (p.users ?? "logged_on"))?.label ?? "ログオン中のユーザー";
}
